// Copyright © 2026 Gian Marco Cappelli — SAPIENS (finsapiens.it). Tutti i diritti riservati. Sviluppato su commissione esclusiva. Vietata la riproduzione senza autorizzazione scritta.
import React from 'react';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import { ExternalLink } from 'lucide-react';

// Deployment 17 — P3 — Confronto Broker
// Pagina educativa, dati indicativi aprile 2026.

const BROKERS = [
  {
    id: 'fineco',
    name: 'Fineco',
    sede: 'Italia',
    regolamentazione: 'Banca d\'Italia, Consob (FITD garanzia €100.000)',
    ideale: 'Chi vuole tutto in una banca: investimenti, conto corrente, mutui, carte',
    pro: ['Piattaforma molto completa con ricerca e analisi integrate', 'Trading internazionale su 26 mercati', 'Servizio clienti in italiano'],
    contro: ['Costi medi più alti dei broker discount', 'Commissioni minime su molti strumenti'],
    url: 'https://finecobank.com',
  },
  {
    id: 'degiro',
    name: 'DEGIRO',
    sede: 'Olanda (flatexDEGIRO Bank AG, Germania)',
    regolamentazione: 'BaFin (Germania), garanzia depositi €100.000 (FGD tedesco)',
    ideale: 'Investitori passivi che comprano ETF a rotazione',
    pro: ['Commissioni molto basse', 'Lista core di ETF gratuiti (con condizioni)', 'Mercati europei e USA inclusi'],
    contro: ['Interfaccia meno intuitiva di Trade Republic o Scalable', 'Niente IBAN italiano — broker estero in regime dichiarativo'],
    url: 'https://www.degiro.it',
  },
  {
    id: 'trade-republic',
    name: 'Trade Republic',
    sede: 'Germania',
    regolamentazione: 'BaFin (Germania), garanzia depositi €100.000 (FGD tedesco)',
    ideale: 'Principianti che vogliono iniziare con piccole somme e PAC su ETF',
    pro: ['Interfaccia mobile semplicissima', 'PAC su ETF a partire da €1', 'Conto cash con interessi e IBAN tedesco', 'Copertura mediatica ampia in Italia 2025-2026'],
    contro: ['Niente IBAN italiano — regime dichiarativo richiesto', 'Solo app mobile, niente web client maturo'],
    url: 'https://traderepublic.com/it-it',
  },
  {
    id: 'scalable',
    name: 'Scalable Capital',
    sede: 'Germania',
    regolamentazione: 'BaFin (Germania), garanzia depositi €100.000',
    ideale: 'Investitori passivi che vogliono PAC senza commissioni con piano gratuito',
    pro: ['Modello freemium con piano gratuito', 'PAC ETF gratuiti su molti strumenti', 'Robo-advisor opzionale'],
    contro: ['Alcune funzioni avanzate solo su piano Prime+', 'Niente IBAN italiano'],
    url: 'https://it.scalable.capital',
  },
  {
    id: 'xtb',
    name: 'XTB',
    sede: 'Polonia (società quotata)',
    regolamentazione: 'KNF Polonia, passportata in Italia',
    ideale: 'Investitori attivi e passivi che vogliono zero commissioni su ETF',
    pro: ['0 commissioni su ETF e azioni fino a €100.000/mese di volume', 'Ricerca e analisi avanzate', 'Servizio clienti in italiano'],
    contro: ['Modello CFD parallelo può confondere principianti', 'Spread su forex e CFD non sono "zero"'],
    url: 'https://www.xtb.com/it',
  },
  {
    id: 'directa',
    name: 'Directa SIM',
    sede: 'Italia',
    regolamentazione: 'Consob, FITD garanzia €100.000',
    ideale: 'Trader attivi che fanno molte operazioni a giornata',
    pro: ['SIM italiana storica', 'Piattaforma trader-grade', 'Regime amministrato (gestione fiscale automatica)'],
    contro: ['Costi più alti per investitori passivi che fanno poche operazioni', 'Meno indicato per chi vuole solo PAC su ETF'],
    url: 'https://www.directa.it',
  },
];

const ConfrontoBroker = () => (
  <div className="min-h-screen bg-[#1C1C1A]" data-testid="confronto-broker-page">
    <Helmet>
      <title>Confronto Broker 2026 — Quale scegliere per investire? | SAPIENS</title>
      <meta name="description" content="Confronto aggiornato dei principali broker per investitori italiani: Fineco, DEGIRO, Trade Republic, Scalable, XTB, Directa. Dati indicativi aprile 2026." />
      <link rel="canonical" href="https://finsapiens.it/risorse/confronto-broker" />
      <meta property="og:title" content="Confronto Broker 2026 — Quale scegliere per investire? | SAPIENS" />
      <meta property="og:description" content="Confronto aggiornato dei principali broker per investitori italiani." />
    </Helmet>
    <div className="container-wide py-10 max-w-5xl">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-6">
        <h1 className="text-3xl sm:text-4xl font-medium text-[#E8E8E4] mb-3">Confronto Broker 2026</h1>
        <p className="text-[#888780]">Quale broker scegliere per investire? Confronto aggiornato dei 6 principali operatori per il pubblico italiano.</p>
      </motion.div>

      <div className="sapiens-note-box mb-6 text-sm" data-testid="confronto-broker-update-note">
        I dati sono indicativi e aggiornati ad aprile 2026. Le tariffe cambiano frequentemente — verifica sempre le condizioni aggiornate sul sito ufficiale del broker prima di aprire un conto.
      </div>

      {/* Card layout */}
      <div className="space-y-4">
        {BROKERS.map((b) => (
          <div key={b.id} data-testid={`broker-${b.id}`} className="rounded-xl p-5 bg-[#252523] border border-white/[0.06]">
            <div className="flex items-start justify-between flex-wrap gap-3 mb-3">
              <div>
                <h3 className="text-lg font-semibold text-[#E8E8E4]">{b.name}</h3>
                <p className="text-xs text-[#888780] mt-1">{b.sede} · {b.regolamentazione}</p>
              </div>
              <a href={b.url} target="_blank" rel="noreferrer" className="text-sm text-[#C9A84C] hover:underline flex items-center gap-1.5">
                Sito ufficiale <ExternalLink className="w-3.5 h-3.5" />
              </a>
            </div>
            <p className="text-sm text-[#C5C5BE] mb-3"><strong className="text-[#E8E8E4]">Ideale per:</strong> {b.ideale}</p>
            <div className="grid sm:grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-xs text-[#3D7A52] uppercase font-semibold mb-1">Punti di forza</p>
                <ul className="space-y-1 text-[#C5C5BE]">
                  {b.pro.map((p) => <li key={p}>• {p}</li>)}
                </ul>
              </div>
              <div>
                <p className="text-xs text-[#E8903A] uppercase font-semibold mb-1">Punti di attenzione</p>
                <ul className="space-y-1 text-[#C5C5BE]">
                  {b.contro.map((p) => <li key={p}>• {p}</li>)}
                </ul>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="sapiens-note-box mt-8 text-xs" data-testid="confronto-broker-disclaimer">
        Le informazioni presenti in questa pagina hanno scopo esclusivamente educativo e non costituiscono raccomandazione finanziaria. SAPIENS non ha accordi commerciali con nessuno dei broker elencati.
      </div>
    </div>
  </div>
);

export default ConfrontoBroker;
